import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  UploadCloud,
  X,
  Loader2,
  CheckCircle2,
  ArrowUpRight,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { moveTypes } from "@/data/site";
import { Reveal } from "./motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "",
  phone: "",
  email: "",
  service_area: "",
  current_location: "",
  destination: "",
  preferred_date: "",
  preferred_time: "",
  move_type: "",
  rooms_or_items: "",
  has_stairs: false,
  has_elevator: false,
  heavy_items: false,
  has_pets: "",
  needs_packing: "",
  description: "",
  agreed_terms: false,
};

const field =
  "bg-[#1C1C1C] border-[#3d3d3d] text-white placeholder:text-neutral-600 focus-visible:ring-[#FF4500] focus-visible:ring-offset-0 h-12";

export const QuoteForm = () => {
  const [form, setForm] = useState(empty);
  const [photos, setPhotos] = useState([]); // {id, url, original_filename, previewUrl}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);
  const today = new Date().toISOString().split("T")[0];

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const handleFiles = async (files) => {
    const list = Array.from(files).slice(0, 8);
    setUploading(true);
    for (const file of list) {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await axios.post(`${API}/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos((p) => [
          ...p,
          { ...data, previewUrl: URL.createObjectURL(file) },
        ]);
      } catch (err) {
        toast.error(`Could not upload ${file.name}`);
      }
    }
    setUploading(false);
  };

  const removePhoto = (id) => {
    setPhotos((p) => p.filter((ph) => ph.id !== id));
    if (fileRef.current) fileRef.current.value = "";
  };

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name";
    if (!form.phone.trim()) return "Please enter your phone number";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email))
      return "Please enter a valid email address";
    if (!form.service_area) return "Please select your service area";
    if (!form.current_location.trim()) return "Please enter your current location";
    if (!form.destination.trim()) return "Please enter your destination";
    if (!form.move_type) return "Please select a type of move";
    if (!form.agreed_terms) return "Please review and agree to the customer terms";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, {
        ...form,
        photo_ids: photos.map((p) => p.id),
      });
      setDone(true);
      toast.success("Quote request received!");
    } catch (er) {
      toast.error("Something went wrong. Please call or text us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section
        id="quote"
        data-testid="quote-section"
        className="relative z-10 bg-[#262626] py-24 sm:py-32 border-y border-white/5"
      >
        <div className="mx-auto max-w-2xl px-5 sm:px-8 text-center">
          <CheckCircle2
            className="h-16 w-16 text-[#FF4500] mx-auto mb-8"
            strokeWidth={1.5}
          />
          <h2
            data-testid="quote-success"
            className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl"
          >
            Thank you, {form.name.split(" ")[0]}!
          </h2>
          <p className="mt-6 text-neutral-400 text-lg leading-relaxed">
            Your quote request has been received. We'll review the details of
            your move and reach out shortly with next steps. You'll receive your
            work order to e-sign by email. We'll text you the day before your
            move to confirm your arrival window. For anything urgent, call or
            text us directly.
          </p>
          <button
            data-testid="quote-reset-btn"
            onClick={() => {
              setForm(empty);
              setPhotos([]);
              setDone(false);
            }}
            className="mt-10 inline-flex items-center gap-2 border border-white/25 text-white font-semibold px-6 py-3 hover:bg-white hover:text-[#141414] transition-colors"
          >
            Submit another request
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="relative z-10 bg-[#262626] py-24 sm:py-32 border-y border-white/5"
    >
      <div className="mx-auto max-w-5xl px-5 sm:px-8">
        <Reveal className="max-w-2xl mb-12">
          <p className="text-xs tracking-[0.3em] uppercase text-[#FF4500] font-bold mb-4">
            Request a Quote
          </p>
          <h2 className="font-display font-bold uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
            Tell us about your move
          </h2>
          <p className="mt-6 text-neutral-400 leading-relaxed">
            Every move is different. Share a few details and we'll build a quote
            around your distance, items, home size, movers needed, stairs,
            timing, and urgency. No obligation.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            data-testid="quote-form"
            className="grid grid-cols-1 sm:grid-cols-2 gap-5"
          >
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Full name</Label>
              <Input data-testid="input-name" className={field} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Phone</Label>
              <Input data-testid="input-phone" className={field} value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Email</Label>
              <Input data-testid="input-email" type="email" className={field} value={form.email} onChange={set("email")} placeholder="you@email.com" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Service area</Label>
              <Select value={form.service_area} onValueChange={set("service_area")}>
                <SelectTrigger data-testid="select-service-area" className={`${field} data-[placeholder]:text-neutral-600`}>
                  <SelectValue placeholder="Select your service area" />
                </SelectTrigger>
                <SelectContent className="bg-[#303030] border-[#3d3d3d] text-white">
                  {["Dallas–Fort Worth", "Memphis", "Other"].map((t) => (
                    <SelectItem key={t} value={t} className="focus:bg-[#FF4500] focus:text-[#141414]">
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Current location</Label>
              <Input data-testid="input-current-location" className={field} value={form.current_location} onChange={set("current_location")} placeholder="Pickup address / area" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Destination</Label>
              <Input data-testid="input-destination" className={field} value={form.destination} onChange={set("destination")} placeholder="Drop-off address / area" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Preferred date</Label>
              <div className="relative">
                <Input
                  data-testid="input-date"
                  type="date"
                  min={today}
                  className={`${field} [color-scheme:dark] ${!form.preferred_date ? "text-transparent" : ""}`}
                  value={form.preferred_date}
                  onChange={set("preferred_date")}
                />
                {!form.preferred_date && (
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-600 text-sm">
                    Select a date
                  </span>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Preferred time</Label>
              <Select value={form.preferred_time} onValueChange={set("preferred_time")}>
                <SelectTrigger data-testid="input-time" className={`${field} data-[placeholder]:text-neutral-600`}>
                  <SelectValue placeholder="Select a time" />
                </SelectTrigger>
                <SelectContent className="bg-[#303030] border-[#3d3d3d] text-white">
                  {["Morning", "Afternoon", "Evening", "Flexible"].map((t) => (
                    <SelectItem key={t} value={t} className="focus:bg-[#FF4500] focus:text-[#141414]">
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Type of move</Label>
              <Select value={form.move_type} onValueChange={set("move_type")}>
                <SelectTrigger data-testid="select-move-type" className={`${field} data-[placeholder]:text-neutral-600`}>
                  <SelectValue placeholder="Select a move type" />
                </SelectTrigger>
                <SelectContent className="bg-[#303030] border-[#3d3d3d] text-white">
                  {moveTypes.map((t) => (
                    <SelectItem key={t} value={t} className="focus:bg-[#FF4500] focus:text-[#141414]">
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Approx. rooms / items</Label>
              <Input data-testid="input-rooms" className={field} value={form.rooms_or_items} onChange={set("rooms_or_items")} placeholder="e.g. 1 bedroom, ~15 items" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Will any pets be at the property during the move?</Label>
              <Select value={form.has_pets} onValueChange={set("has_pets")}>
                <SelectTrigger data-testid="select-pets" className={`${field} data-[placeholder]:text-neutral-600`}>
                  <SelectValue placeholder="Select an answer" />
                </SelectTrigger>
                <SelectContent className="bg-[#303030] border-[#3d3d3d] text-white">
                  {["Yes", "No"].map((t) => (
                    <SelectItem key={t} value={t} className="focus:bg-[#FF4500] focus:text-[#141414]">
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Do you need help packing?</Label>
              <Select value={form.needs_packing} onValueChange={set("needs_packing")}>
                <SelectTrigger data-testid="select-packing" className={`${field} data-[placeholder]:text-neutral-600`}>
                  <SelectValue placeholder="Select an answer" />
                </SelectTrigger>
                <SelectContent className="bg-[#303030] border-[#3d3d3d] text-white">
                  {["Yes", "No"].map((t) => (
                    <SelectItem key={t} value={t} className="focus:bg-[#FF4500] focus:text-[#141414]">
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-neutral-500 leading-relaxed">
                Billed at the standard hourly rate. Customer provides all boxes and packing materials.
              </p>
            </div>

            <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3 py-2">
              {[
                ["has_stairs", "Stairs involved"],
                ["has_elevator", "Elevator available"],
                ["heavy_items", "Heavy or oversized items (piano, safe, etc.)"],
              ].map(([key, label]) => (
                <label
                  key={key}
                  className="flex items-center gap-3 border border-[#3d3d3d] bg-[#1C1C1C] px-4 py-3 cursor-pointer hover:border-[#FF4500]/50 transition-colors"
                >
                  <Checkbox
                    data-testid={`checkbox-${key}`}
                    checked={form[key]}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, [key]: !!v }))}
                    className="border-[#444] data-[state=checked]:bg-[#FF4500] data-[state=checked]:border-[#FF4500]"
                  />
                  <span className="text-sm text-neutral-300">{label}</span>
                </label>
              ))}
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Describe your move</Label>
              <Textarea
                data-testid="input-description"
                className="bg-[#1C1C1C] border-[#3d3d3d] text-white placeholder:text-neutral-600 focus-visible:ring-[#FF4500] focus-visible:ring-offset-0 min-h-28"
                value={form.description}
                onChange={set("description")}
                placeholder="Heavy or specialty items, parking, timing, anything we should know…"
              />
            </div>

            {/* Photo upload */}
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-neutral-400">
                Photos of your items (optional)
              </Label>
              <div
                data-testid="photo-dropzone"
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  handleFiles(e.dataTransfer.files);
                }}
                className="border border-dashed border-[#454545] bg-[#1C1C1C] p-8 flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-[#FF4500]/60 transition-colors"
              >
                {uploading ? (
                  <Loader2 className="h-7 w-7 text-[#FF4500] animate-spin" />
                ) : (
                  <UploadCloud className="h-7 w-7 text-neutral-400" strokeWidth={1.5} />
                )}
                <p className="text-sm text-neutral-400 text-center">
                  Drag & drop or <span className="text-[#FF4500] font-semibold">browse</span> to upload photos
                </p>
                <p className="text-xs text-neutral-600">PNG, JPG, WEBP · up to 10MB each</p>
                <input
                  ref={fileRef}
                  data-testid="photo-input"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFiles(e.target.files)}
                />
              </div>

              {photos.length > 0 && (
                <div data-testid="photo-previews" className="grid grid-cols-3 sm:grid-cols-5 gap-3 pt-3">
                  {photos.map((p) => (
                    <div key={p.id} className="relative group aspect-square overflow-hidden border border-[#3d3d3d]">
                      <img src={p.previewUrl} alt={p.original_filename} className="h-full w-full object-cover" />
                      <button
                        type="button"
                        data-testid={`remove-photo-${p.id}`}
                        onClick={() => removePhoto(p.id)}
                        className="absolute top-1 right-1 bg-[#1C1C1C]/80 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="sm:col-span-2 pt-2">
              <div data-testid="customer-terms" className="space-y-3 text-sm text-neutral-400 leading-relaxed">
                <p data-testid="deposit-note">
                  A $50 deposit is required to reserve your move date and is
                  applied to your final balance. The $25 booking fee is a
                  separate, non-refundable scheduling fee and is not applied to
                  your balance.
                </p>
                <p>
                  Duke Summers Moving provides moving labor only. Trucks, boxes,
                  tape, and packing materials are provided by the customer. We are
                  not responsible for the contents of boxes packed by the customer.
                </p>
                <p>
                  For junk removal and cleanouts, the customer is responsible for
                  ensuring that any items placed at the curb comply with local bulk
                  trash and collection rules, or for providing a dumpster or
                  trailer. Duke Summers Moving is not responsible for code
                  compliance citations related to curbside placement.
                </p>
                <p>
                  Packing is performed using materials supplied by the customer. If
                  adequate protective materials (such as bubble wrap or packing
                  paper) are not provided for fragile items, those items are packed
                  at the customer's request and risk, and Duke Summers Moving is
                  not liable for resulting breakage. Our liability for any damaged
                  item is limited to $0.60 per pound per item unless otherwise
                  agreed in writing.
                </p>
                <p>
                  Damage claims must be submitted in writing within 7 days of your
                  move, with photos of the damaged item. Proof of the item's weight
                  or value may be required.
                </p>
                <p>
                  Cancellations made 48 hours or more before your scheduled move
                  receive a full deposit refund. Cancellations made less than 48
                  hours before your move forfeit the deposit. For bookings made
                  less than 48 hours before the move, the deposit is non-refundable
                  once paid. If our crew arrives and cannot begin work because the
                  truck, container, or property is not ready or accessible, the
                  2-hour minimum will be charged.
                </p>
                <p>
                  All pets must be secured in a closed room, crate, or yard for the
                  entire job. Our crew may pause work until pets are secured, and
                  billed time continues during the pause.
                </p>
                <p>
                  For two-location jobs, loading time is due before the crew travels
                  to the unloading location.
                </p>
                <p>
                  The remaining balance is due at job completion, before the crew
                  leaves.
                </p>
              </div>
              <label
                data-testid="agree-terms-label"
                className="mt-5 flex items-start gap-3 border border-[#3d3d3d] bg-[#1C1C1C] p-4 cursor-pointer hover:border-[#FF4500]/50 transition-colors"
              >
                <Checkbox
                  data-testid="checkbox-agree-terms"
                  checked={form.agreed_terms}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, agreed_terms: !!v }))}
                  className="mt-0.5 border-[#555] data-[state=checked]:bg-[#FF4500] data-[state=checked]:border-[#FF4500]"
                />
                <span className="text-sm text-neutral-200 leading-relaxed">
                  I have read and agree to the customer terms above, including the
                  cancellation policy and liability limits.
                </span>
              </label>
              <p
                data-testid="response-time-line"
                className="mt-4 text-sm text-neutral-400 leading-relaxed"
              >
                Prefer to talk? Call or text 972-947-6017. We respond to every
                quote request the same day.
              </p>
              <button
                type="submit"
                data-testid="quote-submit-btn"
                disabled={submitting}
                className="group mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#FF4500] text-[#141414] font-bold px-10 py-4 text-base hover:bg-[#E03C00] transition-colors disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send Quote Request
                    <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </>
                )}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
